/**
 * YouTubeThumbnailPanel — the rich "Custom thumbnail" panel for YouTube Publish.
 *
 * Renders the SAME model-aware image config as Posts & Carousels and
 * Text-to-Image by composing the shared <ImageGeneratorPanel>: provider, model
 * (when supported), resolution (default YouTube 1280x720), styles (when
 * supported), advanced settings, plus a variation-count slider. The generate →
 * save lifecycle is one-shot (the thumbnail endpoint returns variations
 * synchronously), so the panel has no poller; the shared progress bar + generic
 * error panel still render via <ImageGeneratorPanel>.
 *
 * Backend contract (shared/routes/ai_generation.py `GenerateRequest`): by
 * default the thumbnail's concept is derived from the video title/description by
 * the server-side prompt optimizer. The user can instead opt to write their own
 * prompt via the "Write my own prompt" toggle — when on, the panel shows the
 * shared prompt input and sends `custom_prompt` (+ optional
 * `custom_negative_prompt`), and the backend uses that verbatim and skips the
 * LLM title analysis. Styles map to `preferences.fooocus_style_override`;
 * resolution maps to `width`/`height`.
 *
 * The YouTube Data API only accepts one thumbnail upload per video (unlike the
 * Studio UI's A/B thumbnail testing, which isn't exposed via the API), so the
 * user explicitly picks one variation with a checkbox toggle (never
 * auto-applied). Checking it persists
 * the image via save-to-job-assets under the primary language; unchecking
 * removes that file via clear-from-job-assets. The invariant is honest:
 * "a variation is checked" ⟺ "a thumbnail file exists on disk" ⟺ "the publish
 * job will upload it". Regenerating, or changing the primary language, clears
 * any previously-applied file so a thumbnail the user didn't pick is never
 * uploaded.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { ImageGeneratorPanel } from '../image/ImageGeneratorPanel';
import type { ImageGenRunner } from '../image/ImageGeneratorPanel';
import { MultiImageUploader } from '../image/MultiImageUploader';
import { imageGenerationAPI } from '../../services/imageGenerationAPI';
import { apiFailureError } from '../../utils/httpUtils';
import { createDefaultImageConfig } from '../../constants/imageSource';
import type { ImageGenerationConfig, ImagePromptInput } from '../../types';
import { SegmentedPicker } from '../common';
import {
  clearThumbnailFromJobAssets,
  createPublishWorkingDir,
  generateThumbnail,
  saveThumbnailToJobAssets,
  type GenerateThumbnailVariation,
  type SaveThumbnailToJobAssetsBody,
} from '../../services/youtubePublishApi';

/** YouTube recommended thumbnail size. */
const YOUTUBE_THUMB_WIDTH = 1280;
const YOUTUBE_THUMB_HEIGHT = 720;
const YOUTUBE_THUMB_RESOLUTION = `${YOUTUBE_THUMB_WIDTH}x${YOUTUBE_THUMB_HEIGHT}`;
const MIN_VARIATIONS = 1;
const MAX_VARIATIONS = 5;

interface YouTubeThumbnailPanelProps {
  /** Title used as the thumbnail concept source. */
  title: string;
  /** Description used as additional concept context. */
  description: string;
  /** Server-side assets root; minted on demand when empty. */
  jobAssetsRoot: string;
  /** Called when a working dir is minted so the parent reuses it on submit. */
  onJobAssetsRootChange: (root: string) => void;
  /** Primary target language — the thumbnail is saved under it (one per video). */
  primaryLanguage: string;
  /**
   * Overlay the title text on the thumbnail. False for a multilingual publish
   * (one thumbnail for all languages → must be purely visual, no text).
   */
  includeText: boolean;
  /**
   * Notifies the parent when a thumbnail is applied (path) or cleared (null), so
   * it can warn before submit if "Custom thumbnail" is on but none was picked.
   */
  onThumbnailSaved?: (savedPath: string | null) => void;
}

export const YouTubeThumbnailPanel = memo(function YouTubeThumbnailPanel({
  title,
  description,
  jobAssetsRoot,
  onJobAssetsRootChange,
  primaryLanguage,
  includeText,
  onThumbnailSaved,
}: YouTubeThumbnailPanelProps) {
  const { t } = useTranslation();

  // Full model-aware config, seeded at the YouTube thumbnail resolution.
  const [config, setConfig] = useState<ImageGenerationConfig>(() =>
    createDefaultImageConfig({
      resolution: YOUTUBE_THUMB_RESOLUTION,
      width: YOUTUBE_THUMB_WIDTH,
      height: YOUTUBE_THUMB_HEIGHT,
    })
  );
  const [variationCount, setVariationCount] = useState(1);

  // When on, the user writes their own image prompt (shown via ImageGeneratorPanel's
  // PromptInput, stored in config.prompt) instead of letting the backend derive the
  // concept from the title/description. Off = current AI-from-title behaviour.
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);

  // Generation mode: 'txt2img' (default, concept from title or own prompt) vs
  // 'img2img' (reference image + prompt via Fooocus image-prompt). img2img reuses
  // the SAME uploader + endpoint as the Image tab's "Image prompt" (RULE #-2).
  const [imageMode, setImageMode] = useState<'txt2img' | 'img2img'>('txt2img');
  const [refImages, setRefImages] = useState<ImagePromptInput[]>([]);
  const validRefImages = useMemo(() => refImages.filter((img) => img.image), [refImages]);

  // Generated variations + which one the user has applied (checkbox toggle).
  // selectedIndex !== null  ⟺  a thumbnail file exists on disk for this video.
  const [variations, setVariations] = useState<GenerateThumbnailVariation[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [clearing, setClearing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Resolve width/height to send: explicit custom dims when set, else parse the
  // selected preset (e.g. "1280x720"). Falls back to the YouTube default.
  const resolveDimensions = useCallback(
    (cfg: ImageGenerationConfig): { width: number; height: number } => {
      if (cfg.width && cfg.height) return { width: cfg.width, height: cfg.height };
      const match = /^(\d+)x(\d+)$/.exec(cfg.resolution);
      if (match) return { width: Number(match[1]), height: Number(match[2]) };
      return { width: YOUTUBE_THUMB_WIDTH, height: YOUTUBE_THUMB_HEIGHT };
    },
    []
  );

  // Persist one variation under the primary language (checkbox checked). The
  // backend resolves the image source (data URL / HTTPS URL / server path); the
  // browser never fetches or decodes the image.
  const selectVariation = useCallback(
    async (variation: GenerateThumbnailVariation, index: number): Promise<void> => {
      setSavingIndex(index);
      setSaveError(null);
      try {
        let assetsRoot = jobAssetsRoot;
        if (!assetsRoot) {
          const { working_dir } = await createPublishWorkingDir();
          assetsRoot = working_dir;
          onJobAssetsRootChange(working_dir);
        }

        const url = variation.imageUrl;
        const savePayload: SaveThumbnailToJobAssetsBody = {
          job_assets_root: assetsRoot,
          language_code: primaryLanguage,
        };
        if (url.startsWith('data:')) {
          savePayload.image_data_url = url;
        } else if (url.startsWith('https://')) {
          savePayload.image_url = url;
        } else if (url.startsWith('http://')) {
          setSaveError(t('youtube.publish.thumbnail.errorInsecureUrl'));
          setSavingIndex(null);
          return;
        } else {
          savePayload.image_path = url;
        }

        const result = await saveThumbnailToJobAssets(savePayload);
        setSelectedIndex(index);
        onThumbnailSaved?.(result.saved_path);
      } catch (err) {
        // Selection is left unchanged on failure (no false "applied" state).
        setSaveError(
          err instanceof Error ? err.message : t('youtube.publish.thumbnail.errorGeneric')
        );
      } finally {
        setSavingIndex(null);
      }
    },
    [jobAssetsRoot, onJobAssetsRootChange, primaryLanguage, onThumbnailSaved, t]
  );

  // Deselect (checkbox unchecked): remove the saved file so the publish job does
  // NOT upload it. Keep the selection on failure — the file may still exist, so
  // claiming it's cleared would be a lie.
  const deselectVariation = useCallback(async (): Promise<void> => {
    setSaveError(null);
    // Nothing was ever persisted (no working dir) → purely local clear.
    if (!jobAssetsRoot) {
      setSelectedIndex(null);
      onThumbnailSaved?.(null);
      return;
    }
    setClearing(true);
    try {
      await clearThumbnailFromJobAssets({
        job_assets_root: jobAssetsRoot,
        language_code: primaryLanguage,
      });
      setSelectedIndex(null);
      onThumbnailSaved?.(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : t('youtube.publish.thumbnail.errorClear'));
    } finally {
      setClearing(false);
    }
  }, [jobAssetsRoot, primaryLanguage, onThumbnailSaved, t]);

  // One-shot runner: generate variations. Nothing is auto-applied — the user
  // explicitly checks one afterwards. Before generating, drop any previously
  // applied thumbnail (state + on-disk file) so a stale pick isn't uploaded
  // after a regenerate where the user doesn't choose a new one.
  const runner = useMemo<ImageGenRunner<GenerateThumbnailVariation[]>>(
    () => ({
      start: async (cfg) => {
        setVariations([]);
        setSelectedIndex(null);
        onThumbnailSaved?.(null);
        setSaveError(null);
        if (jobAssetsRoot) {
          // Best-effort: a failed clear must not block regeneration.
          try {
            await clearThumbnailFromJobAssets({
              job_assets_root: jobAssetsRoot,
              language_code: primaryLanguage,
            });
          } catch {
            /* ignore — regenerate proceeds regardless */
          }
        }
        const { width, height } = resolveDimensions(cfg);
        const trimmedPrompt = cfg.prompt.trim();
        const trimmedNegative = cfg.negative_prompt.trim();

        // img2img: transform the uploaded reference image(s) via Fooocus
        // image-prompt (same endpoint as the Image tab's "Image prompt"). The
        // result is adapted into the thumbnail variation shape so the pick/save
        // flow below is identical. The generate button is gated on >=1 image +
        // a non-empty prompt (requirePrompt), so neither is blank here.
        if (imageMode === 'img2img') {
          const ipResp = await imageGenerationAPI.imagePromptGenerate({
            prompt: trimmedPrompt,
            negative_prompt: trimmedNegative || undefined,
            images: validRefImages,
            styles: cfg.styles.length > 0 ? cfg.styles : undefined,
            resolution: `${width}x${height}`,
            async_process: false,
          });
          if (!ipResp.success || !ipResp.data) {
            // Keeps status / error.code so ImageGeneratorPanel can tell a
            // billing refusal (402) from a generation failure.
            throw apiFailureError(ipResp, t('youtube.publish.thumbnail.errorGeneric'));
          }
          const imgs = Array.isArray(ipResp.data.images) ? ipResp.data.images : [];
          const adapted: GenerateThumbnailVariation[] = imgs.map((img, i) => ({
            id: `img2img-${Date.now()}-${i}`,
            index: i,
            imageUrl: img.url,
            thumbnailUrl: img.url,
            metadata: {},
          }));
          if (adapted.length === 0) {
            throw new Error(t('youtube.publish.thumbnail.errorGeneric'));
          }
          return { done: true, result: adapted, progress: { progress: 100, message: '' } };
        }

        // txt2img: concept from the title/description, or the user's own prompt.
        const resp = await generateThumbnail({
          title,
          description: description || undefined,
          variation_count: variationCount,
          provider: cfg.provider,
          model: cfg.model,
          width,
          height,
          // Multilingual publish (includeText=false) -> text-free image, since one
          // thumbnail serves every language.
          text_overlay: includeText,
          custom_prompt: useCustomPrompt && trimmedPrompt ? trimmedPrompt : undefined,
          custom_negative_prompt: useCustomPrompt && trimmedNegative ? trimmedNegative : undefined,
          preferences: {
            fooocus_style_override: cfg.styles.length > 0 ? cfg.styles : undefined,
          },
        });
        const generated = resp.result?.variations ?? [];
        if (generated.length === 0) {
          throw new Error(t('youtube.publish.thumbnail.errorGeneric'));
        }
        return { done: true, result: generated, progress: { progress: 100, message: '' } };
      },
    }),
    [
      title,
      description,
      variationCount,
      resolveDimensions,
      includeText,
      useCustomPrompt,
      imageMode,
      validRefImages,
      jobAssetsRoot,
      primaryLanguage,
      onThumbnailSaved,
      t,
    ]
  );

  const handleComplete = useCallback((generated: GenerateThumbnailVariation[]) => {
    // Show the candidates; the user picks one explicitly (no auto-apply).
    setVariations(generated);
  }, []);

  // The saved file is named <primaryLanguage>.png. If the primary language
  // changes, a previously applied thumbnail no longer corresponds — clear the
  // stale file (under the OLD language) and the selection so we don't show a
  // checked box for a thumbnail that won't be uploaded.
  const prevPrimaryRef = useRef(primaryLanguage);
  useEffect(() => {
    const prev = prevPrimaryRef.current;
    if (prev === primaryLanguage) return;
    prevPrimaryRef.current = primaryLanguage;
    if (selectedIndex !== null) {
      if (jobAssetsRoot) {
        void clearThumbnailFromJobAssets({
          job_assets_root: jobAssetsRoot,
          language_code: prev,
        }).catch(() => {
          /* best-effort cleanup of the stale-language file */
        });
      }
      setSelectedIndex(null);
      onThumbnailSaved?.(null);
    }
  }, [primaryLanguage, jobAssetsRoot, selectedIndex, onThumbnailSaved]);

  const busy = savingIndex !== null || clearing;

  return (
    <ImageGeneratorPanel<GenerateThumbnailVariation[]>
      config={config}
      onConfigChange={setConfig}
      runner={runner}
      onComplete={handleComplete}
      // The thumbnail endpoint (GenerateRequest) honors provider, model, styles
      // (preferences.fooocus_style_override), width/height (resolution),
      // variation_count below, and an optional user-authored custom_prompt. It has
      // NO steps/guidance/seed/sharpness, so we hide the Advanced block rather than
      // show controls the backend ignores (RULE #22). The Prompt block is shown
      // ONLY when the user toggles "Write my own prompt"; otherwise the concept is
      // derived from the title/description (see headerSlot). requirePrompt gates the
      // generate button on a non-empty prompt in custom mode, so we never silently
      // fall back to the title-derived concept.
      // img2img needs a prompt (describing the desired image); txt2img needs one
      // only when the user opts to write their own. requirePrompt gates the
      // generate button on a non-empty prompt accordingly.
      showPrompt={useCustomPrompt || imageMode === 'img2img'}
      requirePrompt={useCustomPrompt || imageMode === 'img2img'}
      showAdvanced={false}
      errorStage="thumbnail_generation"
      // img2img is gated on >=1 reference image (concept comes from it, not the
      // title); txt2img is gated on a source title.
      disabled={imageMode === 'img2img' ? validRefImages.length === 0 : !title.trim()}
      generateLabel={t('youtube.publish.thumbnail.generateButton')}
      data-testid="youtube-thumbnail-generator"
      headerSlot={
        <>
          {/* Persistent notice — one thumbnail per video. */}
          <div className="bg-purple-900/10 border border-purple-500/20 text-purple-200/80 text-xs px-3 py-2 rounded">
            {t('youtube.publish.thumbnail.singlePerVideoNotice')}
          </div>

          {/* Mode: text-to-image (default) vs image prompt (image-to-image). */}
          <SegmentedPicker
            label={t('youtube.publish.thumbnail.modeLabel', { defaultValue: 'Source' })}
            size="sm"
            layout="equal"
            options={[
              {
                value: 'txt2img',
                label: t('youtube.publish.thumbnail.modeText', { defaultValue: 'Text to image' }),
              },
              {
                value: 'img2img',
                label: t('youtube.publish.thumbnail.modeImage', { defaultValue: 'Image prompt' }),
              },
            ]}
            value={imageMode}
            onChange={(v) => setImageMode(v as 'txt2img' | 'img2img')}
          />

          {imageMode === 'img2img' ? (
            <div className="space-y-2">
              <p className="text-white/50 text-xs">
                {t('youtube.publish.thumbnail.img2imgNote', {
                  defaultValue:
                    'Upload a reference image and describe the thumbnail in the prompt below (image-to-image uses Fooocus). The title is overlaid separately.',
                })}
              </p>
              <MultiImageUploader images={refImages} onChange={setRefImages} maxImages={2} />
            </div>
          ) : (
            /* Concept source: AI-from-title (default) OR a user-authored prompt.
               Toggling on reveals the shared PromptInput below (showPrompt). */
            <label className="flex items-start gap-2.5 cursor-pointer min-h-[44px] sm:min-h-0">
              <input
                type="checkbox"
                checked={useCustomPrompt}
                onChange={(e) => setUseCustomPrompt(e.target.checked)}
                className="mt-0.5 h-4 w-4 flex-shrink-0 accent-purple-500"
                data-testid="youtube-thumbnail-custom-prompt-toggle"
              />
              <span className="flex-1">
                <span className="block text-sm font-medium text-white">
                  {t('youtube.publish.thumbnail.customPromptToggle', {
                    defaultValue: 'Write my own prompt',
                  })}
                </span>
                <span className="block text-white/50 text-xs mt-0.5">
                  {useCustomPrompt
                    ? t('youtube.publish.thumbnail.customPromptOnNote', {
                        defaultValue:
                          'Your prompt describes the image directly — the AI won’t derive a concept from the title. Don’t include on-image text; the title is overlaid separately.',
                      })
                    : t('youtube.publish.thumbnail.conceptFromTitleNote', {
                        defaultValue:
                          'The thumbnail concept is generated from the video title and description above. Choose a provider, styles and resolution below.',
                      })}
                </span>
              </span>
            </label>
          )}
        </>
      }
      extraControls={
        <div>
          <label className="form-label">
            {t('youtube.publish.thumbnail.variations', { defaultValue: 'Variations' })}:{' '}
            {variationCount}
          </label>
          <input
            type="range"
            min={MIN_VARIATIONS}
            max={MAX_VARIATIONS}
            value={variationCount}
            onChange={(e) => setVariationCount(Number(e.target.value))}
            className="w-full accent-purple-500"
            data-testid="youtube-thumbnail-variation-count"
          />
          <div className="flex justify-between text-[11px] text-white/40 mt-1">
            <span>{MIN_VARIATIONS}</span>
            <span>{MAX_VARIATIONS}</span>
          </div>
        </div>
      }
    >
      {/* Disabled-reason hint: txt2img needs a source title (concept comes from
          the title/description); img2img needs at least one reference image. */}
      {imageMode === 'txt2img' && !title.trim() && (
        <p className="text-amber-300/80 text-xs">{t('youtube.publish.thumbnail.needTitleHint')}</p>
      )}
      {imageMode === 'img2img' && validRefImages.length === 0 && (
        <p className="text-amber-300/80 text-xs">
          {t('youtube.publish.thumbnail.needRefImageHint', {
            defaultValue: 'Upload at least one reference image to generate.',
          })}
        </p>
      )}

      {/* Variation results — check one to apply it (YouTube uses a single
          thumbnail). Unchecking removes it so nothing is uploaded. */}
      {variations.length > 0 && (
        <div className="space-y-3" data-testid="youtube-thumbnail-results">
          <p className="text-white/60 text-xs">
            {variations.length > 1
              ? t('youtube.publish.thumbnail.pickOne', {
                  defaultValue: 'Check one thumbnail to use for this video:',
                })
              : t('youtube.publish.thumbnail.singleResult', {
                  defaultValue: 'Generated thumbnail — check “Use this” to apply it:',
                })}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {variations.map((variation, index) => {
              const isSelected = selectedIndex === index;
              const isSaving = savingIndex === index;
              const isClearingThis = clearing && isSelected;
              return (
                <div
                  key={variation.id}
                  className={`bg-black/30 rounded-lg overflow-hidden border ${
                    isSelected
                      ? 'border-purple-500/60 ring-1 ring-purple-500/40'
                      : 'border-white/10'
                  }`}
                >
                  <img
                    src={variation.thumbnailUrl || variation.imageUrl}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                  <div className="flex items-center justify-between gap-2 px-2 py-2">
                    <span className="text-[11px] text-white/50">#{index + 1}</span>
                    {/* Checkbox toggle (app standard: accent-purple-500). Check to
                        apply, uncheck to remove. */}
                    <label
                      className={`flex items-center gap-2 min-h-[36px] ${
                        busy ? 'cursor-wait opacity-60' : 'cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={busy}
                        onChange={() => {
                          if (isSelected) {
                            void deselectVariation();
                          } else {
                            void selectVariation(variation, index);
                          }
                        }}
                        className="w-4 h-4 accent-purple-500"
                        data-testid={`youtube-thumbnail-use-${index}`}
                      />
                      <span className="text-xs text-white/80">
                        {isSaving
                          ? t('common.processing')
                          : isClearingThis
                            ? t('youtube.publish.thumbnail.removing', { defaultValue: 'Removing…' })
                            : isSelected
                              ? t('youtube.publish.thumbnail.usingThis', {
                                  defaultValue: 'Using this',
                                })
                              : t('youtube.publish.thumbnail.useThis', {
                                  defaultValue: 'Use this',
                                })}
                      </span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>

          {saveError && <p className="text-red-400 text-xs">{saveError}</p>}
          {selectedIndex !== null && !busy && (
            <p className="text-green-400 text-xs flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5" />
              {t('youtube.publish.thumbnail.saved')}
            </p>
          )}
        </div>
      )}
    </ImageGeneratorPanel>
  );
});

export default YouTubeThumbnailPanel;
